/**
 * Design-Tokens — Single Source of Truth fuer Card-/Pill-/Empty-State-/Tabellen-
 * Klassen. Wurde in 2026-05-25 angelegt nachdem ein App-weiter Konsistenz-Audit
 * zwei dominante Card-Varianten gefunden hat (rounded-md/bg-zinc-900/30/p-4 vs.
 * rounded-lg/bg-zinc-900/40/p-5) und unzaehlige variierende Header-/Empty-
 * State-/Tabellen-Header-Patterns.
 *
 * Ziel: alle neuen Cards nutzen die Tokens hier. Bestehende werden gradually
 * migriert — siehe "Phase-2-Migration"-Liste im Audit-Bericht 2026-05-25.
 *
 * Verwendung:
 *   import { CARD, CARD_HEADER, CARD_HINT } from '@/lib/design-tokens';
 *   <section className={CARD}>
 *     <h3 className={CARD_HEADER}><Icon className="size-4" /> Titel</h3>
 *     <p className={CARD_HINT}>Erlaeuterung.</p>
 *     ...
 *   </section>
 *
 * Achtung: Watchlist-Badges, HoverTooltip, LoadingProgressInline, News-AI-
 * Summary + News-Spikes-Deep-Dive sind FRISCH und werden bewusst nicht
 * migriert (siehe Audit).
 */

// --- CARD-WRAPPER ----------------------------------------------------------
// Standard-Card (kompakt, Dashboard-Pattern): rounded-md + p-4
export const CARD = 'rounded-md border border-zinc-800/80 bg-zinc-900/30 p-4';
// Prominent-Card (Symbol-Detail-Pattern): rounded-lg + bg-zinc-900/40 + p-5
export const CARD_PROMINENT =
  'rounded-lg border border-zinc-800/80 bg-zinc-900/40 p-5';
// Action-Card (W70 Phase 6) — Bloomberg-Style: zusaetzlich 2px-Top-Border in
// cockpit-accent-Farbe um den User auf die wichtigsten Aktion-Sektionen
// hinzuweisen (PositionHealth, TopPicks). Subtle, kein Schrei.
export const CARD_ACTION =
  'rounded-md border border-zinc-800/80 border-t-2 border-t-cockpit-accent/40 bg-zinc-900/30 p-4';
// KPI-Tile (W70 Phase 10) — kompakter Card-Variant fuer KPI-Zahlen-Kacheln
// (Journal-Stats, Dashboard-Mini-KPIs). Identisch zu CARD aber p-3 statt p-4.
export const KPI_TILE =
  'rounded-md border border-zinc-800/80 bg-zinc-900/30 p-3';
// Card-Header — fuer h3 mit Icon
export const CARD_HEADER =
  'text-sm font-semibold text-zinc-200 flex items-center gap-1.5';
// Card-Sub-Text — Hint unter dem Header
export const CARD_HINT = 'text-[11px] text-zinc-500 leading-snug';

// --- PILL / BADGE ----------------------------------------------------------
// Pill-Basis-Klassen (z.B. fuer Watchlist-Badges) — px-1.5 py-0.5 + size-2.5 Icons
export const PILL_BASE =
  'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-mono shrink-0';

// --- SEKTION / DIVIDER -----------------------------------------------------
export const SECTION_DIVIDER = 'border-t border-zinc-800/60';

// --- TABLE -----------------------------------------------------------------
// Konsistenter Table-Header-Style
export const TABLE_HEADER =
  'text-[10px] uppercase tracking-wider text-zinc-500';

// --- EMPTY STATE -----------------------------------------------------------
// Empty-State Text (typisch in einer Card, wenn keine Daten da sind)
export const EMPTY_STATE = 'text-xs text-zinc-400 leading-snug';

// --- TONE-SCALE ------------------------------------------------------------
// Semantische Farb-Tokens fuer bullish/bearish/neutral/mixed-States.
// Genutzt von Score-Pills, Sentiment-Indicators, Macro-Bias etc.
export const TONE = {
  bullish: {
    color: 'text-emerald-300',
    bg: 'bg-emerald-950/30',
    border: 'border-emerald-800/50',
  },
  bearish: {
    color: 'text-red-300',
    bg: 'bg-red-950/30',
    border: 'border-red-800/50',
  },
  neutral: {
    color: 'text-zinc-300',
    bg: 'bg-zinc-950/40',
    border: 'border-zinc-700/50',
  },
  mixed: {
    color: 'text-amber-300',
    bg: 'bg-amber-950/30',
    border: 'border-amber-800/50',
  },
} as const;

export type ToneVariant = keyof typeof TONE;
